import { NavLink } from "react-router-dom";
import {
  Home,
  FileText,
  ShoppingCart,
  Users,
  Package,
  Settings,
  CircleUser,
  LogOut,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { Logo } from "./Logo";
import { Avatar } from "./Avatar";
import { useAuthStore } from "@/lib/auth-store";
import { doLogout } from "@/lib/session";

const items = [
  { to: "/", label: "Home", icon: Home, exact: true },
  { to: "/invoices", label: "Invoices", icon: FileText },
  { to: "/purchases", label: "Purchases", icon: ShoppingCart },
  { to: "/clients", label: "Clients", icon: Users },
  { to: "/services", label: "Services", icon: Package },
  { to: "/settings", label: "Settings", icon: Settings },
  { to: "/profile", label: "Profile", icon: CircleUser },
];

export function SideNav() {
  const user = useAuthStore((s) => s.user);

  return (
    <aside
      className="hidden lg:flex fixed left-0 top-0 bottom-0 w-60 flex-col bg-[var(--color-sidebar)] text-[var(--color-sidebar-fg)]"
      aria-label="Primary"
    >
      {/* soft brand glow at the top, mirrors the hub sidebar */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-40 opacity-70"
        style={{
          background:
            "radial-gradient(120% 80% at 20% 0%, color-mix(in srgb, var(--color-sidebar-primary) 20%, transparent), transparent 70%)",
        }}
      />
      <div className="relative px-5 py-5 border-b border-[var(--color-sidebar-border)]">
        <Logo withWordmark tone="light" />
      </div>

      <nav className="relative flex-1 overflow-y-auto px-3 py-3">
        <div className="mb-1 px-2 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-sidebar-muted)]">
          Workspace
        </div>
        <ul className="space-y-0.5">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <li key={it.to}>
                <NavLink
                  to={it.to}
                  end={it.exact}
                  className={({ isActive }) =>
                    cn(
                      "group relative my-0.5 flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[14px] font-medium transition-all duration-150",
                      isActive
                        ? "bg-gradient-to-r from-[var(--color-sidebar-primary)]/20 via-[var(--color-sidebar-accent)]/70 to-[var(--color-sidebar-accent)]/40 text-white elev-1"
                        : "text-[var(--color-sidebar-fg)]/75 hover:bg-[var(--color-sidebar-accent)]/60 hover:text-[var(--color-sidebar-fg)]",
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      {isActive ? (
                        <span className="absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-full bg-[var(--color-sidebar-primary)] shadow-[0_0_8px_var(--color-sidebar-primary)]" />
                      ) : null}
                      <Icon
                        size={18}
                        aria-hidden
                        className={cn(
                          "shrink-0 transition-colors",
                          isActive
                            ? "text-[var(--color-sidebar-primary)]"
                            : "text-[var(--color-sidebar-muted)] group-hover:text-[var(--color-sidebar-fg)]",
                        )}
                      />
                      <span className="truncate">{it.label}</span>
                    </>
                  )}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="relative border-t border-[var(--color-sidebar-border)] p-3">
        <div className="flex w-full items-center gap-2.5 rounded-xl bg-[var(--color-sidebar-accent)]/40 px-2.5 py-2 text-left">
          <Avatar
            user={user}
            size={32}
            className="bg-[var(--color-sidebar-primary)] text-[13px] text-[var(--color-sidebar)]"
          />
          <div className="min-w-0 flex-1">
            <div className="truncate text-[13px] font-medium text-[var(--color-sidebar-fg)]">
              {user?.name ?? "Account"}
            </div>
            <div className="truncate text-[11px] text-[var(--color-sidebar-muted)]">
              {user?.email}
            </div>
          </div>
          <button
            aria-label="Log out"
            title="Log out"
            onClick={() => void doLogout()}
            className="shrink-0 rounded-md p-1.5 text-[var(--color-sidebar-muted)] transition-colors hover:bg-[var(--color-sidebar-accent)]/70 hover:text-[var(--color-sidebar-fg)]"
          >
            <LogOut size={16} aria-hidden />
          </button>
        </div>
      </div>
    </aside>
  );
}
